import React from 'react';
import { Link } from 'react-router-dom';
import { motion, useMotionValue, useTransform, useReducedMotion } from 'framer-motion';
import { Star, ShieldCheck, BookOpenCheck, Award } from 'lucide-react';
import { useLang } from '../../context/LanguageContext';

/**
 * Premium Poocho Hero — v3 (Feb 20, 2026 Final Luxury Polish)
 *
 * Delta vs v2:
 *  - REMOVED SVG turbulence noise (user asked: "Background Clean रहे, Noise बिल्कुल न हो")
 *  - ADDED multi-layer luxury depth: 3 stacked radial glows behind Acharya (rim light + warm glow + gold halo)
 *  - ADDED slow floating photo motion (breathing 8s cycle)
 *  - ADDED poetic Devanagari kicker line above headline for emotional storytelling
 *  - LARGER typography scale (text-[76px] lg headline)
 *  - CTA gets animated soft-glow pulse + arrow micro-interaction
 *  - Trust cards → glass-morphism with gold top-accent line + larger 32px icons
 *  - Scroll indicator hint at bottom (calm bouncing chevron)
 *  - Motion curve everywhere: cubic-bezier [0.22, 1, 0.36, 1] (Apple-style luxury out-quart)
 */
export default function PremiumPoochoHero({ compact = false, ctaHref = null, ctaTestId = 'premium-hero-cta' }) {
  const easeLux = [0.22, 1, 0.36, 1];
  const { lang } = useLang();
  const isHi = lang !== 'en';

  // Desktop-only parallax (4-6deg tilt on mouse move). Disabled on mobile + reduced-motion.
  const reduce = useReducedMotion();
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  // Max 5deg tilt — subtle luxury
  const rotateX = useTransform(my, [-0.5, 0.5], [5, -5]);
  const rotateY = useTransform(mx, [-0.5, 0.5], [-5, 5]);

  const handleParallax = (e) => {
    if (reduce || typeof window === 'undefined') return;
    // Skip on touch devices / narrow screens
    if (window.matchMedia('(pointer: coarse)').matches || window.innerWidth < 1024) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const x = (e.clientX - rect.left) / rect.width - 0.5;
    const y = (e.clientY - rect.top) / rect.height - 0.5;
    mx.set(x);
    my.set(y);
  };
  const resetParallax = () => {
    mx.set(0);
    my.set(0);
  };

  return (
    <section
      className="relative pt-16 sm:pt-24 lg:pt-32 pb-16 lg:pb-28 overflow-hidden"
      data-testid="premium-poocho-hero"
    >
      {/* === CINEMATIC MULTI-LAYER BACKDROP === */}
      {/* Base obsidian */}
      <div className="absolute inset-0 bg-[#050505]" />
      {/* Royal purple aura — top right */}
      <div className="absolute -top-[280px] -right-[220px] w-[880px] h-[880px] rounded-full bg-[#3d101e]/50 blur-[220px] pointer-events-none" />
      {/* Deep magenta undertone — center left */}
      <div className="absolute top-1/2 -left-[300px] -translate-y-1/2 w-[720px] h-[720px] rounded-full bg-[#5a1a2e]/28 blur-[200px] pointer-events-none" />
      {/* Warm gold underglow — bottom */}
      <div className="absolute -bottom-[240px] left-1/2 -translate-x-1/2 w-[900px] h-[560px] rounded-full bg-[#D4AF37]/[0.11] blur-[200px] pointer-events-none" />
      {/* Very light nebula wisp — subtle purple stroke */}
      <div className="absolute top-[10%] left-[35%] w-[520px] h-[220px] rounded-full bg-[#5E1E88]/12 blur-[140px] pointer-events-none opacity-70" />
      {/* Vignette darken edges for depth */}
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,transparent_45%,rgba(0,0,0,0.55)_100%)] pointer-events-none" />

      {/* Floating golden dust particles — luxury ambient */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden opacity-70">
        {[
          { x: '18%', y: '22%', s: 2, d: 0 },
          { x: '82%', y: '35%', s: 3, d: 1.2 },
          { x: '38%', y: '68%', s: 2, d: 0.6 },
          { x: '65%', y: '78%', s: 3, d: 2.1 },
          { x: '12%', y: '60%', s: 2, d: 1.8 },
          { x: '90%', y: '20%', s: 2, d: 0.3 },
          { x: '55%', y: '15%', s: 3, d: 2.6 },
          { x: '28%', y: '85%', s: 2, d: 1.5 },
        ].map((p, i) => (
          <motion.span
            key={i}
            className="absolute rounded-full bg-[#D4AF37] shadow-[0_0_8px_rgba(212,175,55,0.7)]"
            style={{ left: p.x, top: p.y, width: p.s, height: p.s }}
            animate={{ opacity: [0, 0.85, 0], y: [0, -22, 0] }}
            transition={{ duration: 6 + p.d, repeat: Infinity, delay: p.d, ease: 'easeInOut' }}
          />
        ))}
      </div>

      <div className="container-custom relative z-10 max-w-6xl mx-auto">
        {/* Breadcrumb / back */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.9, ease: easeLux }}
          className="mb-12 lg:mb-20"
        >
          <Link
            to="/"
            className="inline-flex items-center gap-1.5 text-[#8A8A8A] hover:text-[#D4AF37] text-[13px] tracking-[0.1em] transition-colors duration-500"
            data-testid="premium-hero-home-link"
          >
            ← {isHi ? 'होम' : 'Home'}
          </Link>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-10 items-center">
          {/* === LEFT (col-span-5): Text === */}
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1.1, ease: easeLux, delay: 0.15 }}
            className="lg:col-span-5 text-center lg:text-left order-2 lg:order-1"
          >
            {/* Sacred kicker line */}
            <div className="mb-8 lg:mb-9">
              <div className="inline-flex flex-col items-center lg:items-start gap-3">
                {/* Small gold line + label */}
                <div className="flex items-center gap-3">
                  <span className="h-px w-8 bg-gradient-to-r from-[#D4AF37]/60 to-transparent" />
                  <span className="text-[10px] uppercase tracking-[0.4em] text-[#D4AF37]/85 font-medium">
                    पूछो Guruji · ₹99
                  </span>
                </div>
                {/* Poetic sub-kicker — emotional storytelling */}
                <p className="text-[13px] sm:text-[14px] text-[#B5B5B5]/85 italic font-light tracking-[0.06em] max-w-[380px] leading-relaxed">
                  {isHi ? '“जहाँ शास्त्र मिलता है श्रद्धा से...”' : '“Where Shastra meets Śraddhā…”'}
                </p>
              </div>
            </div>

            {/* Headline — Final Locked Copy */}
            {isHi ? (
              <h1
                className="font-heading text-[38px] leading-[1.08] sm:text-[52px] lg:text-[60px] lg:leading-[1.06] tracking-[-0.02em] text-[#F5F5F5] font-light mb-8 lg:mb-9"
                data-testid="premium-hero-headline"
              >
                आपकी एक चिंता,
                <br />
                <span className="text-gold-gradient font-normal">आचार्य विवेक जी</span> का
                <br />
                <span className="text-gold-gradient font-normal">शास्त्रसम्मत मार्गदर्शन।</span>
              </h1>
            ) : (
              <h1
                className="font-heading text-[40px] leading-[1.05] sm:text-[54px] lg:text-[64px] lg:leading-[1.02] tracking-[-0.02em] text-[#F5F5F5] font-light mb-8 lg:mb-9"
                data-testid="premium-hero-headline"
              >
                Your Concern.
                <br />
                <span className="text-gold-gradient font-normal">Authentic Vedic Guidance</span>
                <br />
                by <span className="text-gold-gradient font-normal">Acharya Vivek Ji.</span>
              </h1>
            )}

            {/* Single subhead — cleaner spacing */}
            <p className="text-[#B5B5B5] text-[15px] sm:text-[17px] leading-[1.85] font-light max-w-[460px] mx-auto lg:mx-0 mb-10 lg:mb-12">
              {isHi ? (
                <>
                  33 वर्षों का पराशरीय अनुभव · Swiss Ephemeris की सटीकता।
                  <br />
                  मात्र <span className="text-[#F5F5F5] font-normal">₹99</span> में शास्त्रीय मार्गदर्शन।
                </>
              ) : (
                <>
                  33 years of Parashari experience · Swiss Ephemeris precision.
                  <br />
                  Personalized guidance at just <span className="text-[#F5F5F5] font-normal">₹99</span>.
                </>
              )}
            </p>

            {/* Premium CTA — animated soft-glow */}
            {!compact && (
              <div className="relative inline-block group">
                {/* Pulsing glow layer */}
                <motion.div
                  className="absolute -inset-2 rounded-full bg-gradient-to-r from-[#BF953F]/40 via-[#F3E5AB]/35 to-[#997A00]/40 blur-2xl"
                  animate={{ opacity: [0.55, 0.85, 0.55] }}
                  transition={{ duration: 4, repeat: Infinity, ease: 'easeInOut' }}
                />
                {ctaHref ? (
                  <Link
                    to={ctaHref}
                    className="relative inline-flex items-center gap-4 px-10 py-5 rounded-full bg-gradient-to-r from-[#BF953F] via-[#D4AF37] to-[#997A00] text-black font-semibold text-[15px] tracking-[0.02em] shadow-[0_20px_60px_-15px_rgba(212,175,55,0.65)] hover:shadow-[0_24px_80px_-10px_rgba(212,175,55,0.85)] hover:scale-[1.02] transition-[transform,box-shadow] duration-700"
                    data-testid={ctaTestId}
                  >
                    <span>{isHi ? 'अपना प्रश्न पूछें' : 'Ask Your Question'}</span>
                    <span
                      aria-hidden
                      className="w-7 h-7 rounded-full bg-black/25 flex items-center justify-center text-sm group-hover:translate-x-1 transition-transform duration-700"
                    >
                      →
                    </span>
                  </Link>
                ) : (
                  <a
                    href="#poocho-question-picker"
                    onClick={(e) => {
                      e.preventDefault();
                      document
                        .getElementById('poocho-question-picker')
                        ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                    }}
                    className="relative inline-flex items-center gap-4 px-10 py-5 rounded-full bg-gradient-to-r from-[#BF953F] via-[#D4AF37] to-[#997A00] text-black font-semibold text-[15px] tracking-[0.02em] shadow-[0_20px_60px_-15px_rgba(212,175,55,0.65)] hover:shadow-[0_24px_80px_-10px_rgba(212,175,55,0.85)] hover:scale-[1.02] transition-[transform,box-shadow] duration-700"
                    data-testid={ctaTestId}
                  >
                    <span>{isHi ? 'अपना प्रश्न पूछें' : 'Ask Your Question'}</span>
                    <span
                      aria-hidden
                      className="w-7 h-7 rounded-full bg-black/25 flex items-center justify-center text-sm group-hover:translate-x-1 transition-transform duration-700"
                    >
                      →
                    </span>
                  </a>
                )}
              </div>
            )}

            {/* Micro trust below CTA */}
            <p className="mt-8 text-[11px] uppercase tracking-[0.35em] text-[#7A7A7A] font-light">
              सुरक्षित · तत्काल · गोपनीय
            </p>
          </motion.div>

          {/* === RIGHT (col-span-7): Massive Acharya with rim light === */}
          <motion.div
            initial={{ opacity: 0, scale: 0.94 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1.4, ease: easeLux, delay: 0.2 }}
            className="lg:col-span-7 relative flex justify-center lg:justify-end order-1 lg:order-2"
          >
            {/* Slow floating wrapper — breathing motion + desktop parallax */}
            <motion.div
              className="relative w-[320px] h-[320px] sm:w-[440px] sm:h-[440px] lg:w-[600px] lg:h-[600px]"
              style={{ rotateX, rotateY, transformPerspective: 1200, transformStyle: 'preserve-3d' }}
              onMouseMove={handleParallax}
              onMouseLeave={resetParallax}
              animate={{ y: [0, -8, 0] }}
              transition={{ duration: 8, repeat: Infinity, ease: 'easeInOut' }}
            >
              {/* Layer 1 — Widest ambient warm glow (feathers into background) */}
              <div className="absolute inset-[-24%] rounded-full bg-[radial-gradient(circle_at_50%_50%,rgba(212,175,55,0.14)_0%,rgba(212,175,55,0.05)_28%,transparent_58%)] pointer-events-none" />
              {/* Layer 2 — Softer amber rim light */}
              <div className="absolute inset-[-8%] rounded-full bg-[radial-gradient(circle_at_50%_40%,rgba(243,229,171,0.18)_0%,rgba(212,175,55,0.06)_25%,transparent_58%)] pointer-events-none" />
              {/* Layer 3 — Subtle royal-purple bloom hugging silhouette (no hard disc) */}
              <div className="absolute inset-[-2%] rounded-full bg-[radial-gradient(circle_at_50%_58%,rgba(42,8,69,0.5)_0%,rgba(19,4,32,0.28)_38%,transparent_68%)] pointer-events-none" />
              {/* Layer 4 — Warm bottom shadow under portrait (natural anchoring) */}
              <div className="absolute inset-x-[8%] bottom-[-4%] h-[38%] bg-[radial-gradient(ellipse_at_50%_100%,rgba(0,0,0,0.85)_0%,transparent_70%)] pointer-events-none blur-sm" />

              {/* === BROKEN HALO (asymmetric, no perfect circle) === */}
              {/* Arc 1: top-right partial */}
              <motion.div
                className="absolute inset-[6%] pointer-events-none"
                animate={{ rotate: 360 }}
                transition={{ duration: 90, repeat: Infinity, ease: 'linear' }}
              >
                <svg viewBox="0 0 400 400" className="w-full h-full">
                  <defs>
                    <linearGradient id="halo-arc-a" x1="0%" y1="0%" x2="100%" y2="100%">
                      <stop offset="0%" stopColor="#D4AF37" stopOpacity="0" />
                      <stop offset="35%" stopColor="#D4AF37" stopOpacity="0.55" />
                      <stop offset="65%" stopColor="#F3E5AB" stopOpacity="0.7" />
                      <stop offset="100%" stopColor="#D4AF37" stopOpacity="0" />
                    </linearGradient>
                  </defs>
                  {/* Broken outer arc — only ~200deg visible */}
                  <path
                    d="M 200 8 A 192 192 0 1 1 40 268"
                    fill="none"
                    stroke="url(#halo-arc-a)"
                    strokeWidth="0.65"
                    strokeLinecap="round"
                  />
                </svg>
              </motion.div>
              {/* Arc 2: counter-rotating partial */}
              <motion.div
                className="absolute inset-[11%] pointer-events-none opacity-70"
                animate={{ rotate: -360 }}
                transition={{ duration: 130, repeat: Infinity, ease: 'linear' }}
              >
                <svg viewBox="0 0 400 400" className="w-full h-full">
                  <defs>
                    <linearGradient id="halo-arc-b" x1="100%" y1="0%" x2="0%" y2="100%">
                      <stop offset="0%" stopColor="#D4AF37" stopOpacity="0" />
                      <stop offset="45%" stopColor="#D4AF37" stopOpacity="0.4" />
                      <stop offset="100%" stopColor="#D4AF37" stopOpacity="0" />
                    </linearGradient>
                  </defs>
                  <path
                    d="M 60 200 A 140 140 0 0 1 340 200"
                    fill="none"
                    stroke="url(#halo-arc-b)"
                    strokeWidth="0.5"
                    strokeLinecap="round"
                  />
                </svg>
              </motion.div>
              {/* Blur ring — soft luminous */}
              <div className="absolute inset-[14%] rounded-full border border-[#D4AF37]/8 blur-[1px]" />

              {/* Acharya cutout — natural blend, deeply feathered mask, cinematic drop-shadow */}
              <img
                src="/brand/vivek-cutout.png"
                alt="Acharya Vivek Mudgal — Vedic Astrologer"
                className="absolute inset-x-0 bottom-[-2%] mx-auto w-[100%] h-[122%] object-contain object-bottom pointer-events-none select-none"
                style={{
                  filter:
                    'drop-shadow(0 34px 44px rgba(0,0,0,0.75)) drop-shadow(0 -18px 32px rgba(212,175,55,0.28)) drop-shadow(16px 12px 30px rgba(212,175,55,0.12)) drop-shadow(-16px 12px 30px rgba(212,175,55,0.12)) drop-shadow(0 6px 14px rgba(42,8,69,0.55))',
                  WebkitMaskImage:
                    'radial-gradient(ellipse 82% 96% at 50% 40%, #000 45%, rgba(0,0,0,0.85) 65%, rgba(0,0,0,0.45) 82%, transparent 100%)',
                  maskImage:
                    'radial-gradient(ellipse 82% 96% at 50% 40%, #000 45%, rgba(0,0,0,0.85) 65%, rgba(0,0,0,0.45) 82%, transparent 100%)',
                }}
                loading="eager"
                fetchPriority="high"
              />

              {/* Certified emblem — floats naturally, not on a disc */}
              <div className="absolute bottom-[4%] left-1/2 -translate-x-1/2 px-4 py-2 rounded-full bg-black/60 border border-[#D4AF37]/40 backdrop-blur-xl shadow-[0_10px_30px_-6px_rgba(0,0,0,0.7)]">
                <span className="text-[#D4AF37] text-[10px] font-medium tracking-[0.3em] uppercase flex items-center gap-2">
                  <span className="w-1 h-1 rounded-full bg-[#D4AF37] shadow-[0_0_8px_rgba(212,175,55,0.9)]" />
                  {isHi ? 'प्रमाणित आचार्य' : 'Certified Acharya'}
                </span>
              </div>
            </motion.div>
          </motion.div>
        </div>

        {/* === PREMIUM TRUST BAR — glass cards === */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1.1, ease: easeLux, delay: 0.6 }}
          className="grid grid-cols-2 md:grid-cols-4 gap-4 sm:gap-6 mt-20 lg:mt-32"
        >
          <TrustCard icon={Award} value="33+" label="वर्ष अनुभव" sublabel="Since 1992" />
          <TrustCard icon={Star} value="4.9 ★" label="33,000+ Clients" sublabel="Verified reviews" />
          <TrustCard icon={BookOpenCheck} value="पराशरीय" label="शास्त्र-प्रमाणित" sublabel="BPHS · फलदीपिका" />
          <TrustCard icon={ShieldCheck} value="Featured" label="Zee · News18" sublabel="Aaj Tak · India TV" />
        </motion.div>

        {/* Scroll indicator */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 1.5 }}
          className="mt-20 lg:mt-28 flex flex-col items-center gap-2.5 text-[#6E6E6E]"
        >
          <span className="text-[10px] uppercase tracking-[0.4em] font-light">{isHi ? 'नीचे देखें' : 'Scroll'}</span>
          <motion.div
            className="w-px h-8 bg-gradient-to-b from-[#D4AF37]/50 to-transparent"
            animate={{ scaleY: [1, 0.55, 1], opacity: [0.6, 1, 0.6] }}
            transition={{ duration: 2.4, repeat: Infinity, ease: 'easeInOut' }}
            style={{ transformOrigin: 'top' }}
          />
        </motion.div>
      </div>
    </section>
  );
}

const TrustCard = ({ icon: Icon, value, label, sublabel }) => (
  <div
    className="group relative bg-white/[0.025] backdrop-blur-xl border border-white/[0.06] rounded-[28px] p-6 sm:p-8 flex flex-col items-center justify-center text-center hover:border-[#D4AF37]/28 hover:bg-white/[0.035] hover:-translate-y-1 transition-[transform,border-color,background-color] duration-700 shadow-[0_20px_50px_-25px_rgba(0,0,0,0.9)] hover:shadow-[0_28px_70px_-24px_rgba(212,175,55,0.15)]"
    data-testid={`hero-trust-${String(label).toLowerCase().replace(/\s+/g, '-').slice(0, 40)}`}
  >
    {/* Top gold accent hairline */}
    <div className="absolute top-0 left-1/2 -translate-x-1/2 w-16 h-px bg-gradient-to-r from-transparent via-[#D4AF37]/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-700" />
    {/* Faint corner glow */}
    <div className="absolute -top-8 -right-8 w-24 h-24 rounded-full bg-[#D4AF37]/[0.04] blur-2xl pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity duration-700" />

    <div className="relative w-12 h-12 sm:w-14 sm:h-14 rounded-full bg-gradient-to-br from-[#D4AF37]/[0.12] to-transparent flex items-center justify-center mb-5 border border-[#D4AF37]/15">
      <Icon className="w-6 h-6 sm:w-7 sm:h-7 text-[#D4AF37]" strokeWidth={1.3} />
    </div>
    <div className="text-[#F5F5F5] text-xl sm:text-2xl font-light tracking-tight leading-none mb-1.5">
      {value}
    </div>
    <div className="text-[11px] sm:text-[12px] text-[#B5B5B5] tracking-[0.08em] leading-tight font-normal">
      {label}
    </div>
    {sublabel && (
      <div className="text-[9.5px] text-[#7A7A7A] tracking-[0.15em] uppercase mt-2 font-light">
        {sublabel}
      </div>
    )}
  </div>
);
